// Shared between ToimenpiteetTekstimuodossa.jsx (screen) and
// renderToimenpiteetTekstimuodossa.js (PDF) so the flattening/bucketing
// logic can never drift between the two renderings — this is genuinely new
// logic (no existing utility in this codebase explodes the sections/items/
// values PTS shape into per-year rows), unlike the small per-file helpers
// (getArea, fmtEur) that follow this app's existing precedent of staying
// duplicated per renderer (see CapexYhteenveto.jsx / renderCapexYhteenveto.js).

// Same guard as Tekniikkataulut.js's own isNumeric — item.values[yearIdx] is
// a free-text string and can hold non-numeric text (e.g. someone typed
// "OPEX" into a year's cost cell instead of a number).
export const isNumeric = (v) =>
  typeof v === "number" ||
  (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v)));

// Same 4 categories and order as CapexYhteenveto.jsx's own CATEGORIES.
export const CATEGORY_SOURCES = [
  { key: "tekniikkaData", name: "Rakennetekniikka" },
  { key: "lviData", name: "LVI Järjestelmät" },
  { key: "sahkoData", name: "Sähköjärjestelmät" },
  { key: "tutkimusData", name: "Lisätutkimukset" },
];

// Same short form used everywhere — subtotal rows, PDF bucket bars, and
// the bigger standalone heading shown above each table.
const BUCKET_LABELS = ["0–1 v", "2–5 v", "6–10 v"];

// 0 -> "0–1 v", 1..5 -> "2–5 v", 6..10 -> "6–10 v" — identical yearIdx
// scheme to CapexYhteenveto.jsx's own footerTotals.slice(0,1)/.slice(1,6)/.slice(6,11).
function bucketIndexOf(yearIdx) {
  if (yearIdx === 0) return 0;
  if (yearIdx <= 5) return 1;
  return 2;
}

// Flattens all 4 categories' items × 11 years into one row per non-empty
// cell. An empty cell produces no row at all (not a 0 € row) — emitting a
// row for every (item, year) combination regardless of whether anything
// was ever planned for most years would blow up row counts for no reason.
function flattenToRows(ptsContext) {
  const { years = [] } = ptsContext || {};
  const rows = [];

  CATEGORY_SOURCES.forEach((src) => {
    const sections = ptsContext?.[src.key] || [];
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        const itemLabel = (item.label || "").trim();
        if (!itemLabel) return;
        const kohde = section.name ? `${section.name}: ${itemLabel}` : itemLabel;

        (item.values || []).forEach((rawVal, yearIdx) => {
          if (rawVal === undefined || rawVal === null || String(rawVal).trim() === "") return;
          // No real sum marked for this year (free text like "tarpeen
          // mukaan", or a literal 0) — nothing to list, and leaving it in
          // would otherwise list the same kohde once per such year with
          // no cost attached to it.
          if (!isNumeric(rawVal) || Number(rawVal) === 0) return;
          rows.push({
            id: `${src.name}-${section.name || ""}-${itemLabel}-${yearIdx}`,
            yearIdx,
            year: years[yearIdx],
            category: src.name,
            kohde,
            // Stored values are in "x1000€" units, as entered in the PTS
            // tables (VAT-inclusive) — same figure, just converted to euros,
            // no VAT math applied (the ALVITON setting is label-only, see
            // ToimenpiteetTekstimuodossa.jsx/renderToimenpiteetTekstimuodossa.js).
            costEur: Number(rawVal) * 1000,
          });
        });
      });
    });
  });

  return rows;
}

const CATEGORY_ORDER = CATEGORY_SOURCES.map((s) => s.name);

// Flattens + buckets + sorts + sums. Returns display-ready euro figures —
// callers never need to know about the x1000 convention.
export function buildBucketedRows(ptsContext) {
  const rows = flattenToRows(ptsContext);

  const buckets = BUCKET_LABELS.map((label, bucketIdx) => {
    const bucketRows = rows
      .filter((r) => bucketIndexOf(r.yearIdx) === bucketIdx)
      .sort((a, b) => a.year - b.year || CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
    const subtotalEur = bucketRows.reduce((sum, r) => sum + (r.costEur || 0), 0);
    return { label, rows: bucketRows, subtotalEur };
  });

  const grandTotalEur = buckets.reduce((sum, b) => sum + b.subtotalEur, 0);
  return { buckets, grandTotalEur };
}

// Leading list, shown before the 0–1 v table — sourced from
// ReportTemplateContainer.jsx's groupedToimenpiteet.immediate (every
// ToimenpideEhdotukset item flagged "Välitön", across the whole report,
// already deduped by text within each category). No year or cost column —
// these are urgent/pre-sale items, not part of the 10-year PTS forecast
// the rest of this block summarizes, so there's nothing to bucket by year
// or sum.
export function buildImmediateRows(groupedToimenpiteet) {
  const immediateByCategory = groupedToimenpiteet?.immediate || {};
  const items = Object.values(immediateByCategory).flat();

  const rows = items.map((item) => ({
    id: `immediate-${item.id}`,
    category: item.category || "Muu",
    kohde: item.sectionName ? `${item.sectionName}: ${item.text}` : item.text,
  }));

  return { label: "Välittömästi / ennen kauppaa", rows };
}

// Duplicated (not shared) from CapexYhteenveto.jsx's own sumArea — matches
// this codebase's existing precedent of each block keeping its own private
// copy of this small helper rather than sharing one.
export function getArea(rakennusData, mergedRakennusData, field) {
  const dataToUse = mergedRakennusData?.rakennukset_fulls?.length > 0 ? mergedRakennusData : rakennusData || {};
  const buildings = Array.isArray(dataToUse.rakennukset_fulls) ? dataToUse.rakennukset_fulls : [];
  const total = buildings
    .filter((r) => r.isMainBuilding)
    .reduce((sum, r) => {
      const val = Number(String(r[field] || "").replace(/\s/g, "").replace(",", "."));
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  return total || 1; // divide-by-zero guard, same fallback as CapexYhteenveto.jsx
}
